import {
  Box,
  CardContent,
  Collapse,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { forwardRef, useContext, useEffect, useState } from "react";
import { BentoBrand } from "schemas/dashboard";

import { trackEvent } from "utils/tracking";
import { useCollection } from "utils/useCollection";

import BrandBody from "../../DetailedBrandView/components/BrandBody";
import { useBrandDetails } from "../../DetailedBrandView/components/BrandBody/useBrandDetails";
import BrandMetadata from "../../DetailedBrandView/components/BrandMetadata";
import BrandIcons from "../../Shared/BrandIcons";
import BrandSocialLinks from "../../Shared/BrandSocialLinks";
import PitchedIcon from "../../Shared/PitchedIcon";
import BrandCardActions from "../BrandCard/BrandCardActions";
import { BrandCardSource } from "../schema";
import AboutSummary from "./AboutSummary";
import BrandLogo from "./BrandLogo";
import FavoriteIconButton from "./FavoriteIconButton";
import styles from "./styles";

interface BrandCardProps {
  brand: BentoBrand;
}

export default forwardRef<HTMLDivElement, BrandCardProps>(({ brand }, ref) => {
  const { collapseDrawerIfOpened } = useContext(QuickSendDrawerContext);
  const { setBrandNavigationHistory } = useContext(DiscoverViewContext);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMediumLargeScreen = useMediaQuery(
    "(min-width:1200px) and (max-width:1400px)",
  );
  const isLargeScreen = useMediaQuery(
    "(min-width:1400px) and (max-width:1760px)",
  );
  const { discoverTab } = useContext(DiscoverViewContext);

  const isSmallWidth =
    (isMediumScreen && discoverTab !== null) ||
    (isMediumLargeScreen && discoverTab === null) ||
    (isLargeScreen && discoverTab !== null);

  const source = BrandCardSource.DISCOVER_BRAND;
  const { isDraftBrand, isAutogeneratedDraftBrand } = useCollection();

  const isDrafted = isDraftBrand(brand);
  const isAutogeneratedDraft = isAutogeneratedDraftBrand(brand);

  const handleClickCard = () => {
    trackEvent("Brand Card Clicked", {
      "Bento Brand ID": brand?.id,
      source,
    });
    setBrandNavigationHistory([brand]);
    setExpanded(!expanded);
    collapseDrawerIfOpened();
  };

  const {
    fetchSelectedBentobrand,
    selectedBrand,
    fetchLoading,
    similarBrands,
    fetchSimilarLoading,
  } = useBrandDetails({ brandDetailsId: brand?.id });

  useEffect(() => {
    if (brand?.id && expanded) {
      fetchSelectedBentobrand(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id, expanded]);

  const renderExpandedBrandBody = () => {
    return (
      <Collapse in={expanded}>
        <CardContent>
          <BrandBody
            selectedBrand={selectedBrand}
            fetchLoading={fetchLoading}
            similarBrands={similarBrands}
            fetchSimilarLoading={fetchSimilarLoading}
            source={source}
            mobileOverride={isSmallWidth}
          />
        </CardContent>
      </Collapse>
    );
  };

  if (isSmallScreen) {
    return (
      <Box
        onClick={handleClickCard}
        ref={ref}
        sx={[styles.brandCard, { p: 2 }]}
        key={brand?.id}
        component="div"
      >
        <Grid container justifyContent={"space-between"} sx={{ mb: 1 }}>
          <Grid item xs container alignItems={"flex-start"} gap={1}>
            <BrandLogo brand={brand} />
            <Box>
              <Typography noWrap sx={{ maxWidth: 150 }}>
                {brand.brandName}
              </Typography>
              <BrandSocialLinks
                instagramFollowerCount={brand.instagramFollowerCount}
                instagramUsername={brand.instagramUsername}
                bentoBrandId={brand.id}
                website={brand.website}
                source={source}
              />
            </Box>
          </Grid>

          <Grid container item xs="auto" alignItems={"center"}>
            <FavoriteIconButton brand={brand} />
            <BrandCardActions source={source} brand={brand} />
          </Grid>
        </Grid>

        <AboutSummary bentoBrand={brand} expanded={expanded} />
        <Box display="flex" flexDirection="row" gap={0.5}>
          <BrandMetadata selectedBrand={brand} noGrid />
        </Box>
        {expanded && renderExpandedBrandBody()}
      </Box>
    );
  }

  return (
    <Box
      onClick={handleClickCard}
      ref={ref}
      sx={styles.brandCard}
      key={brand?.id}
      component="div"
    >
      <Box display="flex" width="100%">
        <Box sx={[styles.brandContent, { minHeight: "275px" }]}>
          <Box sx={styles.brandInnerContent}>
            <Box sx={styles.logoContainer}>
              <BrandLogo brand={brand} />

              <Box sx={styles.brandNameContainer}>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  wrap="nowrap"
                >
                  <Grid item xs zeroMinWidth>
                    <Typography variant="h6" sx={styles.brandName}>
                      {brand.brandName}
                      {(isDrafted || isAutogeneratedDraft) && (
                        <>
                          ,{" "}
                          {isAutogeneratedDraft ? (
                            <Box component="span" sx={styles.reco}>
                              Reco{" "}
                              <Box
                                component="i"
                                className="fa-solid fa-wand-magic-sparkles"
                              />
                            </Box>
                          ) : (
                            <Box component="span" sx={styles.draft}>
                              Draft
                            </Box>
                          )}
                        </>
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs="auto">
                    <FavoriteIconButton brand={brand} />
                  </Grid>
                </Grid>

                <BrandIcons bentoBrand={brand} />

                <BrandSocialLinks
                  instagramFollowerCount={brand.instagramFollowerCount}
                  instagramUsername={brand.instagramUsername}
                  bentoBrandId={brand.id}
                  website={brand.website}
                  source={source}
                />
              </Box>
            </Box>

            <Divider sx={styles.divider} />

            <Box
              display="flex"
              flexDirection="row"
              gap={0.5}
              sx={styles.metadataContainer}
            >
              <BrandMetadata selectedBrand={brand} noGrid />
            </Box>

            <Box sx={{ cursor: "pointer", mt: 1 }}>
              <AboutSummary bentoBrand={brand} expanded={expanded} />
            </Box>

            <Box sx={{ flexShrink: 0, mt: "auto", mr: -1.5 }}>
              <Grid container alignItems="center" justifyContent="flex-end">
                <Grid item container alignItems="center" xs="auto">
                  {brand.emailedBrand && (
                    <Grid item xs="auto" sx={{ mr: 0.5 }}>
                      <PitchedIcon size="small" />
                    </Grid>
                  )}
                  <BrandCardActions source={source} brand={brand} />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      {expanded && renderExpandedBrandBody()}
    </Box>
  );
});
